import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Package, Plus, Search, Users, Download } from 'lucide-react'
import { api, type Client, type ClientGender } from '@/lib/api'
import { downloadClientCsv } from '@/lib/api'
import { ClientAvatar } from '@/components/ClientAvatar'

/** 头像上限 2MB，与服务端一致（前端先拦截，避免无效上传） */

/** 新建/编辑学员提交的字段（gender/age 不填时提交 null 表示清除） */
export interface ClientFormBody {
  name: string
  email?: string
  phone?: string
  notes?: string
  gender: ClientGender | null
  age: number | null
}

export function ClientsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Client | null>(null)
  const [showForm, setShowForm] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.getClients(),
    // 全局 staleTime 为 5 分钟；进入学员页时强制刷新一次，
    // 保证课包增删后剩余课时/状态立即正确
    refetchOnMount: 'always',
  })

  // 保存学员字段（onSuccess 只负责刷新；关闭弹窗统一在 handleSubmit 里按上传结果决定）
  const create = useMutation({
    mutationFn: api.createClient,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<Client> }) => api.updateClient(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const del = useMutation({
    mutationFn: api.deleteClient,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  /**
   * 保存流程：先提交学员字段拿到 id，若选了头像文件再上传；
   * 头像上传失败只提示错误，学员数据已保留
   */
  // OPT-045：R2 头像暂未启用（暂存 D1 数据即可），跳过头像上传
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSubmit = async (body: ClientFormBody, _avatarFile: File | null) => {
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, body })
      } else {
        await create.mutateAsync(body)
      }
      qc.invalidateQueries({ queryKey: ['clients'] })
      setShowForm(false)
      setEditing(null)
    } catch (err) {
      alert((err as Error).message)
    }
  }

  const items = (data?.items || []).filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.email || '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">{t('clients.title')}</h1>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-1" /> {t('clients.new')}
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t('clients.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <Card><CardContent className="p-6 text-center text-slate-500">{t('common.loading')}</CardContent></Card>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Users className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 mb-4">{t('clients.empty')}</p>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-1" /> {t('clients.addFirst')}
          </Button>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => {
            return (
              <Card key={c.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <ClientAvatar client={c} size={32} />
                      <CardTitle className="text-base truncate">{c.name}</CardTitle>
                    </div>
                    {!c.isActive && <Badge variant="secondary">{t('clients.status.inactive')}</Badge>}
                  </div>
                </CardHeader>
                <CardContent className="text-sm text-slate-600 space-y-2">
                  <p className="text-xs text-slate-500 h-4">
                    {[c.gender ? t(`clients.gender.${c.gender}`) : null, c.age != null ? String(c.age) : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  <p className="truncate h-5">{c.email || ''}</p>
                  <p className="h-5">{c.phone || ''}</p>
                  {/* OPT-025：学员卡片显示课包概况 */}
                  <p className="text-xs text-slate-600 pt-1 truncate h-5">
                    {c.packageSummary ? c.packageSummary.replace(/ · /g, '  |  ') : ''}
                  </p>
                  <div className="flex gap-2 pt-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setEditing(c)}>
                      {t('common.edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const url = `${window.location.origin}/h5/${c.magicLinkToken}`
                        navigator.clipboard?.writeText(url)
                        alert(t('clients.linkCopied', { url }))
                      }}
                    >
                      {t('clients.copyLink')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => { if (confirm(t('clients.deleteConfirm'))) del.mutate(c.id) }}
                    >
                      {t('common.delete')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* key 随编辑对象变化，强制表单重挂载，避免残留上一个学员的旧值 */}
      <ClientForm
        key={editing?.id ?? 'new'}
        open={showForm || !!editing}
        client={editing}
        onClose={() => { setShowForm(false); setEditing(null) }}
        onSubmit={handleSubmit}
      />
    </div>
  )
}

/** 学员编辑弹窗内课包区块（OPT-023）：列表 + 从模板加课 */
function ClientPackagesSection({ clientId }: { clientId: number }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [selectedTpl, setSelectedTpl] = useState<number | ''>('')

  const { data: pkgData } = useQuery({ queryKey: ['client-packages', clientId], queryFn: () => api.getClientPackagesById(clientId) })
  const { data: tplData } = useQuery({ queryKey: ['package-templates'], queryFn: () => api.getPackageTemplates() })
  const packages = pkgData?.items || []
  const templates = tplData?.items || []

  const addPkg = useMutation({
    mutationFn: (tpl: typeof templates[number]) => api.createPackage({ clientId, name: tpl.name, totalSessions: tpl.totalSessions, templateId: tpl.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['client-packages', clientId] }); qc.invalidateQueries({ queryKey: ['clients'] }); setAdding(false); setSelectedTpl('') },
  })

  const handleAddFromTemplate = () => {
    if (selectedTpl === '') return
    const tpl = templates.find((t) => t.id === selectedTpl)
    if (tpl) addPkg.mutate(tpl)
  }

  return (
    <div data-testid="client-packages-section" className="border-t pt-3 mt-1">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-700"><Package className="h-3 w-3 inline mr-1" />{t('packages.title')}</span>
        {templates.length > 0 && (
          adding ? (
            <div className="flex gap-1">
              <select value={selectedTpl} onChange={(e) => setSelectedTpl(e.target.value ? Number(e.target.value) : '')} className="text-xs border rounded px-1 py-0.5">
                <option value="">{t('packages.selectTemplate')}</option>
                {templates.map((tpl) => <option key={tpl.id} value={tpl.id}>{tpl.name} ({tpl.totalSessions}{t('packages.sessionsUnit')})</option>)}
              </select>
              <Button type="button" size="sm" variant="outline" onClick={handleAddFromTemplate} disabled={selectedTpl === ''} data-testid="add-package-from-template">{t('packages.addFromTemplate')}</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(false)}>{t('common.cancel')}</Button>
            </div>
          ) : (
            <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="show-add-package">
              <Plus className="h-3 w-3 mr-1" />{t('packages.addPackage')}
            </Button>
          )
        )}
      </div>
      {packages.length === 0 ? (
        <p className="text-xs text-slate-400">{t('packages.noClientPackages')}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {packages.map((p) => (
            <li key={p.id} className="flex justify-between py-0.5 border-b border-slate-50 last:border-0">
              <span>{p.name} ({p.remainingSessions}/{p.totalSessions})</span>
              <span className="text-xs text-slate-400">{t(`packages.status.${p.status}`)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ClientForm({
  open, client, onClose, onSubmit,
}: {
  open: boolean
  client: Client | null
  onClose: () => void
  onSubmit: (body: ClientFormBody, avatarFile: File | null) => void
}) {
  const { t } = useTranslation()
  // 初始值取自 client；key 变化时组件重挂载，这里会重新执行
  const [name, setName] = useState(client?.name || '')
  const [email, setEmail] = useState(client?.email || '')
  const [phone, setPhone] = useState(client?.phone || '')
  const [notes, setNotes] = useState(client?.notes || '')
  const [gender, setGender] = useState<ClientGender | ''>(client?.gender || '')
  const [age, setAge] = useState(client?.age != null ? String(client.age) : '')
  // OPT-045：R2 头像暂未启用，移除头像选择相关 state/handler
  // OPT-015：email 必填的前端拦截错误（不发请求）
  const [emailError, setEmailError] = useState<string | null>(null)

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{client ? t('clients.edit') : t('clients.new')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => {
          e.preventDefault()
          // OPT-015：email 必填——为空直接拦截，不发请求（历史无 email 学员编辑时需补填）
          if (!email.trim()) {
            setEmailError(t('clients.errors.emailRequired'))
            return
          }
          onSubmit({
            name,
            email: email.trim(),
            phone: phone || undefined,
            notes: notes || undefined,
            gender: gender || null,
            age: age === '' ? null : Number(age),
          }, null)
        }}>
          <div className="space-y-3">
            {/* OPT-045：R2 头像暂未启用，隐藏头像上传块（ClientAvatar 以占位显示） */}
            <div>
              <Label htmlFor="name">{t('auth.name')}</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
            </div>
            <div>
              {/* OPT-015：email 必填标识 */}
              <Label htmlFor="email">{t('auth.email')} <span className="text-red-500">*</span></Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (emailError) setEmailError(null)
                }}
                required
              />
              {emailError && (
                <p data-testid="client-form-email-error" className="text-xs text-red-600 mt-1">{emailError}</p>
              )}
            </div>
            <div>
              <Label htmlFor="phone">{t('clients.form.phone')}</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="gender">{t('clients.form.gender')}</Label>
                {/* 原生 select，空值 = 不填（提交 null 表示清除） */}
                <select
                  id="gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value as ClientGender | '')}
                  className="w-full border rounded px-2 py-1 bg-white"
                >
                  <option value="">{t('clients.form.noGenderOption')}</option>
                  <option value="male">{t('clients.gender.male')}</option>
                  <option value="female">{t('clients.gender.female')}</option>
                  <option value="other">{t('clients.gender.other')}</option>
                  <option value="secret">{t('clients.gender.secret')}</option>
                </select>
              </div>
              <div>
                <Label htmlFor="age">{t('clients.form.age')}</Label>
                <Input
                  id="age"
                  type="number"
                  min={0}
                  max={150}
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">{t('common.notes')}</Label>
              <Input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            {/* OPT-023：编辑已有学员时显示课包列表 + 加课包按钮 */}
            {client && <ClientPackagesSection clientId={client.id} />}
            {/* OPT-040：GDPR 单个学员数据导出（档案+课包+预约历史 CSV） */}
            {client && (
              <div className="border-t pt-3 mt-1">
                <Button type="button" variant="outline" size="sm" data-testid="client-export-csv" onClick={() => { void downloadClientCsv(client.id) }}>
                  <Download className="h-3 w-3 mr-1" />{t('clients.exportCsv')}
                </Button>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit">{t('common.save')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
